import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

/**
 * A hook that returns a debounced version of the provided value.
 * The debounced value will only update after the specified delay has passed
 * without the input value changing.
 *
 * Uses lodash/debounce internally and properly handles cleanup on unmount.
 *
 * @param {T} value - The value to debounce
 * @param {number} [delay=300] - The debounce delay in milliseconds
 * @returns {T} The debounced value
 *
 * @template T
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebouncedValue(searchTerm, 300);
 *
 * // debouncedSearch will only update 300ms after searchTerm stops changing
 * useEffect(() => {
 *   if (debouncedSearch) {
 *     fetchResults(debouncedSearch);
 *   }
 * }, [debouncedSearch]);
 */
export function useDebouncedValue(value, delay = 300) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const debouncedSetterRef = useRef(null);

    // Create the debounced setter once, recreate if delay changes
    useEffect(() => {
        debouncedSetterRef.current = debounce((newValue) => {
            setDebouncedValue(newValue);
        }, delay);

        // Cleanup: cancel any pending debounced calls when delay changes or unmount
        return () => {
            if (debouncedSetterRef.current) {
                debouncedSetterRef.current.cancel();
            }
        };
    }, [delay]);

    // Call the debounced setter whenever value changes
    useEffect(() => {
        if (debouncedSetterRef.current) {
            debouncedSetterRef.current(value);
        }

        // Cleanup: cancel pending call if value changes before debounce fires
        return () => {
            if (debouncedSetterRef.current) {
                debouncedSetterRef.current.cancel();
            }
        };
    }, [value]);

    return debouncedValue;
}

export default useDebouncedValue;
